import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';

import {
  getDigitalWalletConfig,
  completeDigitalWalletPayment,
  validateMerchantDigitalWalletPayment,
  DigitalWalletConfigResponse,
  HostedCheckoutCybersourceBillingInfo,
} from '../../../../utils/digitalWalletApi';
import {
  checkoutTxPolling,
  CheckoutTxStatusRequest,
  CheckoutTxStatusResponse,
} from '../api';
import ProcessingPayment from './ProcessingPayment';

export interface DigitalWalletFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  processPayment: () => Promise<void>;
}

interface SessionData {
  sessionId: string;
  businessName?: string;
  currency?: string;
  amount: number;
}

interface DigitalWalletFormProps {
  walletType: 'apple-pay' | 'google-pay';
  sessionData: SessionData;
  onSuccess?: (result: CheckoutTxStatusResponse) => void;
  onError?: (error: unknown) => void;
}

interface GooglePayClient {
  isReadyToPay(request: object): Promise<{result: boolean}>;
  loadPaymentData(request: object): Promise<GooglePayPaymentData>;
}

interface GooglePayBillingAddress {
  name?: string;
  phoneNumber?: string;
  countryCode?: string;
  locality?: string;
  address1?: string;
  address2?: string;
  postalCode?: string;
}

interface GooglePayPaymentData {
  email?: string;
  paymentMethodData: {
    info?: {
      billingAddress?: GooglePayBillingAddress;
    };
    tokenizationData: {
      token: string;
    };
  };
}

interface ApplePayValidateMerchantEvent {
  validationURL: string;
}

interface ApplePayPaymentAuthorizedEvent {
  payment: {
    token: unknown;
  };
}

interface ApplePaySessionInstance {
  onvalidatemerchant:
    | ((event: ApplePayValidateMerchantEvent) => void | Promise<void>)
    | null;
  onerror: ((event: unknown) => void) | null;
  onpaymentmethodselected: ((event: unknown) => void) | null;
  onshippingcontactselected: (() => void) | null;
  onshippingmethodselected: (() => void) | null;
  onpaymentauthorized:
    | ((event: ApplePayPaymentAuthorizedEvent) => void | Promise<void>)
    | null;
  oncancel: (() => void) | null;
  abort(): void;
  begin(): void;
  completeMerchantValidation(session: object): void;
  completePaymentMethodSelection(update: object): void;
  completeShippingContactSelection(update: object): void;
  completeShippingMethodSelection(update: object): void;
  completePayment(status: number): void;
}

interface ApplePaySessionAPI {
  new (version: number, request: object): ApplePaySessionInstance;
  canMakePayments(): boolean;
  readonly STATUS_SUCCESS: number;
  readonly STATUS_FAILURE: number;
}

declare global {
  interface Window {
    google?: {
      payments: {
        api: {
          PaymentsClient: new (options: {
            environment: string;
          }) => GooglePayClient;
        };
      };
    };
    ApplePaySession?: ApplePaySessionAPI;
  }
}

const DigitalWalletForm = forwardRef<
  DigitalWalletFormRef,
  DigitalWalletFormProps
>(({sessionData, walletType, onSuccess, onError}, ref) => {
  const [config, setConfig] = useState<DigitalWalletConfigResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [paymentsClient, setPaymentsClient] = useState<GooglePayClient | null>(
    null
  );
  const [showIframe, setShowIframe] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const applePayAbortReasonRef = useRef<Error | null>(null);

  const submitFormToIframe = useCallback(
    (formData: {
      url: string;
      method?: string;
      parameters?: Record<string, string>;
    }) => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      const form = document.createElement('form');
      form.method = formData.method || 'POST';
      form.action = formData.url;
      form.target = 'payment-iframe';
      form.style.display = 'none';

      if (formData.parameters) {
        Object.entries(formData.parameters).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        });
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    []
  );

  const processTxStatusResponse = useCallback(
    (response: CheckoutTxStatusResponse) => {
      setShowIframe(false);
      setIsPolling(false);

      if (response?.process?.status === 'processed') {
        onSuccess?.(response);
      } else {
        onError?.(
          new Error('Payment ' + (response?.process?.status || 'failed'))
        );
      }
    },
    [onSuccess, onError]
  );

  const startStatusPolling = useCallback(
    (txResponse: {transaction?: {id?: string; processId?: string}}) => {
      if (isPolling) return;

      setIsPolling(true);

      const fetchStatus = async () => {
        try {
          const requestBody: CheckoutTxStatusRequest = {
            txId: txResponse.transaction?.id || '',
            processId: txResponse?.transaction?.processId || '',
            sessionId: sessionData.sessionId,
          };
          const response = await checkoutTxPolling(
            sessionData.sessionId,
            requestBody
          );

          if (response?.process?.status == 'pending') {
            // Still pending, continue polling
            return;
          }

          // Transaction completed
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          processTxStatusResponse(response);
        } catch (error) {
          // setIsPolling(false);
          // if (pollingIntervalRef.current) {
          //   clearInterval(pollingIntervalRef.current);
          //   pollingIntervalRef.current = null;
          // }
          // onError?.(error);
        }
      };

      void fetchStatus();
      pollingIntervalRef.current = setInterval(() => {
        void fetchStatus();
      }, 1000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionData.sessionId, processTxStatusResponse, isPolling]
  );

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    validateAll: () => true,
    hasErrors: () => false,
    processPayment: async () => {
      if (walletType === 'apple-pay') {
        await handleApplePayment();
      } else {
        await handleGooglePayment();
      }
    },
  }));

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await getDigitalWalletConfig(sessionData.sessionId, {
          walletType: walletType === 'apple-pay' ? 'APPLE_PAY' : 'GOOGLE_PAY',
          description: `Payment for ${sessionData.businessName ?? 'Merchant'}`,
        });
        setConfig(response);
      } catch (error) {
        onError?.(error);
      } finally {
        setLoading(false);
      }
    };

    if (!config) {
      void loadConfig();
    }
  }, [
    sessionData.sessionId,
    sessionData.businessName,
    walletType,
    config,
    onError,
  ]);

  useEffect(() => {
    if (walletType === 'google-pay') {
      const loadGooglePay = () => {
        if (window.google?.payments?.api && config?.googlePayConfiguration) {
          const client = new window.google.payments.api.PaymentsClient({
            environment: config.googlePayConfiguration.environment,
          });
          setPaymentsClient(client);

          void client
            .isReadyToPay({
              apiVersion: 2,
              apiVersionMinor: 0,
              allowedPaymentMethods: [
                {
                  type: 'CARD',
                  parameters: {
                    allowedAuthMethods:
                      config.googlePayConfiguration.allowedCardAuthMethods,
                    allowedCardNetworks:
                      config.googlePayConfiguration.allowedCardNetworks,
                  },
                  tokenizationSpecification: {
                    type: 'PAYMENT_GATEWAY',
                    parameters: {
                      gateway: config.googlePayConfiguration.gateway,
                      gatewayMerchantId:
                        config.googlePayConfiguration.gatewayMerchantId,
                    },
                  },
                },
              ],
            })
            .then(() => {
              // Google Pay is ready
            })
            .catch(onError);
        }
      };

      if (!window.google?.payments?.api) {
        const script = document.createElement('script');
        script.src = 'https://pay.google.com/gp/p/js/pay.js';
        script.onload = loadGooglePay;
        document.head.appendChild(script);
      } else {
        loadGooglePay();
      }
    } else if (walletType === 'apple-pay') {
      if (window.ApplePaySession) {
        try {
          window.ApplePaySession.canMakePayments();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Apple Pay canMakePayments() check failed:', error);
        }
      }
    }
  }, [config, walletType, onError]);

  const handleGooglePayment = async () => {
    if (!config?.googlePayConfiguration) {
      onError?.(new Error('Google Pay configuration not available'));
      return;
    }

    if (!paymentsClient) {
      onError?.(new Error('Google Pay not initialized'));
      return;
    }

    const paymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods:
              config.googlePayConfiguration.allowedCardAuthMethods,
            allowedCardNetworks:
              config.googlePayConfiguration.allowedCardNetworks,
            billingAddressRequired: true,
            billingAddressParameters: {
              format: 'FULL',
              phoneNumberRequired: false,
            },
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: config.googlePayConfiguration.gateway,
              gatewayMerchantId:
                config.googlePayConfiguration.gatewayMerchantId,
            },
          },
        },
      ],
      merchantInfo: {
        merchantId: config.googlePayConfiguration.merchantId,
        merchantName: config.googlePayConfiguration.merchantName,
      },
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: sessionData.amount.toString(),
        currencyCode: config.googlePayConfiguration.currencyCode,
        countryCode: config.googlePayConfiguration.countryCode,
      },
    };

    try {
      const paymentData = await paymentsClient.loadPaymentData(
        paymentDataRequest
      );

      // Extract billing info from Google Pay response
      const billingInfo: HostedCheckoutCybersourceBillingInfo | undefined =
        paymentData.paymentMethodData?.info?.billingAddress
          ? {
              firstName:
                paymentData.paymentMethodData.info.billingAddress.name?.split(
                  ' '
                )[0] || '',
              lastName:
                paymentData.paymentMethodData.info.billingAddress.name
                  ?.split(' ')
                  .slice(1)
                  .join(' ') || '',
              email: paymentData.email || '',
              phoneNumber:
                paymentData.paymentMethodData.info.billingAddress.phoneNumber ||
                '',
              country:
                paymentData.paymentMethodData.info.billingAddress.countryCode ||
                '',
              locality:
                paymentData.paymentMethodData.info.billingAddress.locality ||
                '',
              address1:
                paymentData.paymentMethodData.info.billingAddress.address1 ||
                '',
              address2:
                paymentData.paymentMethodData.info.billingAddress.address2 ||
                '',
              postalCode:
                paymentData.paymentMethodData.info.billingAddress.postalCode ||
                '',
            }
          : undefined;

      const result = await completeDigitalWalletPayment(sessionData.sessionId, {
        txId: config.txId,
        walletType: 'GOOGLE_PAY',
        token: paymentData.paymentMethodData.tokenizationData.token,
        billingInfo,
      });

      setShowIframe(true);
      if (result.form?.url) {
        submitFormToIframe(result.form);
      }
      startStatusPolling(result);
    } catch (error) {
      onError?.(error);
    }
  };

  const handleApplePayment = async () => {
    // Reset abort reason for new session
    applePayAbortReasonRef.current = null;

    if (!config?.applePayConfiguration) {
      onError?.(new Error('Apple Pay configuration not available'));
      return;
    }

    if (!window.ApplePaySession) {
      onError?.(new Error('Apple Pay not supported'));
      return;
    }

    // Capture narrowed references before async callbacks
    const ApplePaySessionCtor = window.ApplePaySession;
    const applePayConfig = config.applePayConfiguration;

    const request = {
      countryCode: applePayConfig.countryCode,
      currencyCode: applePayConfig.currencyCode,
      supportedNetworks: applePayConfig.supportedNetworks,
      merchantCapabilities: applePayConfig.merchantCapabilities,
      total: {
        label: applePayConfig.merchantName,
        amount: sessionData.amount.toString(),
      },
    };

    let session: ApplePaySessionInstance;
    try {
      session = new ApplePaySessionCtor(3, request);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create ApplePaySession:', error);
      onError?.(
        new Error(
          'Apple Pay is not available. Please ensure you are using HTTPS and have Apple Pay configured on this device.'
        )
      );
      return;
    }

    session.onvalidatemerchant = async (
      event: ApplePayValidateMerchantEvent
    ) => {
      // eslint-disable-next-line no-console
      console.log('[DigitalWalletForm] onvalidatemerchant called', {
        validationURL: event.validationURL,
        currentDomain: window.location.hostname,
        currentOrigin: window.location.origin,
      });

      try {
        const result = await validateMerchantDigitalWalletPayment(
          sessionData.sessionId,
          {
            validationUrl: event.validationURL,
          }
        );

        // eslint-disable-next-line no-console
        console.log(
          '[DigitalWalletForm] validateMerchantDigitalWalletPayment resolved',
          result
        );

        // Check if result contains an error from Apple
        if (result.statusCode || result.statusMessage) {
          // eslint-disable-next-line no-console
          console.error(
            '[DigitalWalletForm] Merchant validation returned error:',
            result
          );
          applePayAbortReasonRef.current = new Error(
            result.statusMessage || 'Merchant validation failed'
          );
          session.abort();
          return;
        }

        const merchantSession = {
          epochTimestamp: result.epochTimestamp,
          expiresAt: result.expiresAt,
          merchantSessionIdentifier: result.merchantSessionIdentifier,
          nonce: result.nonce,
          merchantIdentifier: result.merchantIdentifier,
          domainName: result.domainName,
          displayName: result.displayName,
          signature: result.signature,
          ...(result.operationalAnalyticsIdentifier && {
            operationalAnalyticsIdentifier:
              result.operationalAnalyticsIdentifier,
          }),
        };

        // Verify domain matches
        const currentDomain = window.location.hostname;
        if (merchantSession.domainName !== currentDomain) {
          // eslint-disable-next-line no-console
          console.error('[DigitalWalletForm] DOMAIN MISMATCH!', {
            merchantSessionDomain: merchantSession.domainName,
            currentDomain: currentDomain,
          });
        }

        session.completeMerchantValidation(merchantSession);
        // eslint-disable-next-line no-console
        console.log(
          '[DigitalWalletForm] completeMerchantValidation called successfully'
        );
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('[DigitalWalletForm] Merchant validation failed:', error);
        applePayAbortReasonRef.current =
          error instanceof Error
            ? error
            : new Error(
                error instanceof Object && 'message' in error
                  ? String(error.message)
                  : 'Merchant validation failed'
              );
        session.abort();
      }
    };

    // Add error handler to catch session-level errors
    session.onerror = (event: unknown) => {
      // eslint-disable-next-line no-console
      console.error('[DigitalWalletForm] Apple Pay session error:', event);
      applePayAbortReasonRef.current = new Error('Apple Pay session error');
    };

    session.onpaymentmethodselected = (_event: unknown) => {
      // eslint-disable-next-line no-console
      console.log('[DigitalWalletForm] onpaymentmethodselected called', _event);
      // Must call this to continue the session
      session.completePaymentMethodSelection({
        newTotal: {
          label: applePayConfig.merchantName,
          amount: sessionData.amount.toString(),
        },
      });
    };

    session.onshippingcontactselected = () => {
      session.completeShippingContactSelection({});
    };

    session.onshippingmethodselected = () => {
      session.completeShippingMethodSelection({});
    };

    session.onpaymentauthorized = async (
      event: ApplePayPaymentAuthorizedEvent
    ) => {
      try {
        const result = await completeDigitalWalletPayment(
          sessionData.sessionId,
          {
            txId: config.txId,
            walletType: 'APPLE_PAY',
            token: JSON.stringify(event.payment.token),
          }
        );

        setShowIframe(true);
        if (result.form?.url) {
          submitFormToIframe(result.form);
        }
        startStatusPolling(result);

        session.completePayment(ApplePaySessionCtor.STATUS_SUCCESS);
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(
          '[DigitalWalletForm] Payment authorization failed:',
          error
        );
        applePayAbortReasonRef.current =
          error instanceof Error
            ? error
            : new Error(
                error instanceof Object && 'message' in error
                  ? String(error.message)
                  : 'Payment failed'
              );
        session.completePayment(ApplePaySessionCtor.STATUS_FAILURE);
      }
    };

    session.oncancel = () => {
      // eslint-disable-next-line no-console
      console.log('[DigitalWalletForm] oncancel called');
      // Use stored abort reason if available, otherwise generic cancel message
      const error =
        applePayAbortReasonRef.current || new Error('Payment cancelled');
      applePayAbortReasonRef.current = null;
      onError?.(error);
    };

    session.begin();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <>
      <iframe ref={iframeRef} name="payment-iframe" style={{display: 'none'}} />
      {showIframe && isPolling ? (
        <ProcessingPayment />
      ) : (
        <div className="space-y-3">
          <div className="text-gray-600 text-sm">
            {walletType === 'apple-pay' ? 'Apple Pay' : 'Google Pay'} ready
          </div>
        </div>
      )}
    </>
  );
});

DigitalWalletForm.displayName = 'DigitalWalletForm';

export default DigitalWalletForm;
